import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { LogErrors } from './../../../../../utilities/LogErrors'

import { InstructorBase } from './../../../../Layouts/Instructor/V2/InstructorBase'
import { Loading } from './../../../../Common/Loading'
import { Search } from './../../../../Common/Tables/Search'

import { ValidateSession } from './../../../../../utilities/BL/Sessions'
import { WEBPAGES } from './../../../../../utilities/EL/WebPages'
import { ROLES } from './../../../../../utilities/EL/Roles'


// Ayuda (Instructor)
// Armazón y lista basados en la estructura de Syllabus.js (validación de sesión +
// InstructorBase + lista con buscador); los documentos son fijos (no vienen de la API),
// agrupados en categorías que se pueden contraer/expandir.

// Manuales en PDF que se abren en una pestaña nueva, agrupados por categoría.
// Nota: "Crear tu cuenta Ztudia y unirte a un grupo o centro" aparece tanto en
// "Instructor" como en "Ztudiante" a propósito (aplica a ambos perfiles).
const helpCategories = [
    {
        title: 'Instructor',
        documents: [
            {
                text: 'Crear tu cuenta Ztudia y unirte a un grupo o centro',
                url: 'https://documents.ztudia.com/MANUALES%20UX/Versi%C3%B3n%205.2/Gu%C3%ADa%20para%20crear%20tu%20cuenta%20Ztudia%20y%20unirte%20a%20un%20grupo%20o%20centro.pdf'
            },
            {
                text: 'Crear grupos y dar de alta alumnos',
                url: 'https://documents.ztudia.com/MANUALES%20UX/Versi%C3%B3n%205.2/Gu%C3%ADa%20para%20crear%20grupos%20y%20dar%20de%20alta%20alumnos.pdf'
            },
            {
                text: 'Agendar evaluaciones',
                url: 'https://documents.ztudia.com/MANUALES%20UX/Versi%C3%B3n%205.2/Gu%C3%ADa%20para%20agendar%20evaluaciones.pdf'
            }
        ]
    },
    {
        title: 'Ztudiante',
        documents: [
            {
                text: 'Crear tu cuenta Ztudia y unirte a un grupo o centro',
                url: 'https://documents.ztudia.com/MANUALES%20UX/Versi%C3%B3n%205.2/Gu%C3%ADa%20para%20crear%20tu%20cuenta%20Ztudia%20y%20unirte%20a%20un%20grupo%20o%20centro.pdf'
            }
        ]
    },
    {
        title: 'Instalación',
        documents: [
            {
                text: 'Instalar ZtudiaApp v5.2 en Windows.',
                url: 'https://documents.ztudia.com/MANUALES%20UX/Versi%C3%B3n%205.2/Manual%20Instalacion%20ZtudiaApp%20v5.2%20en%20Windows.pdf'
            },
            {
                text: 'Instalar ZtudiaApp v5.2 en macOS',
                url: 'https://documents.ztudia.com/MANUALES%20UX/Versi%C3%B3n%205.2/Manual%20Instalacion%20ZtudiaApp%20v5.2%20en%20MAC.pdf'
            },
            {
                text: 'Complemento de instalación y configuración de ZtudiaApp en macOS',
                url: 'https://documents.ztudia.com/MANUALES%20UX/Versi%C3%B3n%205.2/Gu%C3%ADa%20complementaria%20de%20instalaci%C3%B3n%20y%20configuraci%C3%B3n%20de%20ZtudiaApp%20en%20Mac.pdf'
            }
        ]
    }
]

const Help = () => {
    const navigate = useNavigate()

    const [isLoading, setIsLoading] = useState(true)
    const [searchText, setSearchText] = useState('')

    // Estado de contraído/expandido por categoría (por índice). Ausente/false = contraído;
    // true = expandido. Empiezan todas contraídas.
    const [openCategories, setOpenCategories] = useState({})

    const toggleCategory = (categoryIndex) => {
        setOpenCategories((prev) => ({
            ...prev,
            [categoryIndex]: prev[categoryIndex] !== true
        }))
    }

    // Filtra los documentos de cada categoría según la búsqueda, y descarta las
    // categorías que se quedan sin resultados.
    const filteredCategories = useMemo(() => {
        const search = (searchText ?? '').trim().toLowerCase()

        return helpCategories
            .map((category) => ({
                ...category,
                documents: search
                    ? category.documents.filter((document) => document.text.toLowerCase().includes(search))
                    : category.documents
            }))
            .filter((category) => category.documents.length > 0)
    }, [searchText])

    const handleSearch = (value) => {
        setSearchText(value)
    }

    /**
     * Función para obtener la configuración, una vez que termine de cargarse el componente
     */
    useEffect(() => {
        async function Validate() {
            try {
                const tmpSession = await ValidateSession()

                if (tmpSession.success) {
                    if (tmpSession.data.user.userStatus === 2) {
                        setIsLoading(false)
                    } else {
                        navigate('/confirm')
                    }
                } else {
                    navigate(`/?logout=${process.env.REACT_APP_LOGOUT_PARAMETER}`)
                }
            } catch (error) {
                await LogErrors('Validate - Help', error)
            }
        }
        Validate()
    }, []) // eslint-disable-line

    return (
        <>
            {isLoading ? (
                <Loading />
            ) : (
                <InstructorBase urlToGo={'/dashboard'} page={WEBPAGES.INSTRUCTOR.HELP} role={ROLES.INSTRUCTOR}>
                    <section className='container Instructor__Help'>
                        <p className='Instructor__Title Instructor__Title--Center Instructor__SpaceUp'>Ayuda</p>

                        <Search onChange={handleSearch} extraClassName={'Instructor__Syllabus__Search'} />

                        <div>
                            {filteredCategories.length === 0 ? (
                                <p className='Instructor__Text--Center Instructor__SpaceUp'>No se encontró algún documento con la búsqueda realizada. Intenta con otra.</p>
                            ) : (
                                filteredCategories.map((category) => {
                                    // Mientras se busca, se mantienen expandidas las categorías con resultados
                                    // (sin importar su estado manual), para que el usuario vea lo que encontró.
                                    const categoryIndex = helpCategories.findIndex((c) => c.title === category.title)
                                    const isOpen = searchText.trim() !== '' || openCategories[categoryIndex] === true

                                    return (
                                        <div key={category.title} className={`Blocks Instructor__Help__Category ${isOpen ? 'Blocks-Show' : ''}`}>
                                            <div className='Block' onClick={() => toggleCategory(categoryIndex)}>
                                                <div className='Block-Info'>
                                                    <p className='Block-Title'>{category.title}</p>
                                                </div>

                                                <div className='Block-Buttons'>
                                                    <button type='button' className='Block-Buttons-List' onClick={(e) => { e.stopPropagation(); toggleCategory(categoryIndex) }}>
                                                        <i className={`bi bi-caret-${isOpen ? 'up' : 'down'}-fill`}></i>
                                                    </button>
                                                </div>
                                            </div>

                                            {isOpen && (
                                                category.documents.map((document, index) => (
                                                    <div key={index} className='Instructor__Syllabus__CoursesList'>
                                                        <button type='button' className='Instructor__Syllabus__CoursesList__Items Tables--Shadow' onClick={() => window.open(document.url, '_blank', 'noopener,noreferrer')}>
                                                            <span className='Instructor__Syllabus__CoursesList__Items__Text'>{document.text}</span>
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )
                                })
                            )}
                        </div>
                    </section>
                </InstructorBase>
            )}
        </>
    )
}

export { Help };
